import { Folder as FolderIcon, Home } from '@mui/icons-material';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { paths } from '@/paths';
import type { Folder } from '@/types/folder';

interface DocumentBreadcrumbsProps {
  parent: { name: string; id: string };
  ancestors?: Record<string, { name: string; id: string }>;
}

export default function DocumentBreadcrumbs({
  parent,
  ancestors = {},
}: DocumentBreadcrumbsProps): React.JSX.Element | null {
  if (!parent) return null;

  const orderedAncestors = Object.entries(ancestors)
    .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
    .map(([_, value]) => value)
    .reverse();

  return (
    <Breadcrumbs
      separator="/"
      sx={{
        '& .MuiBreadcrumbs-separator': {
          color: 'text.secondary',
          mx: 1,
        },
      }}
    >
      <Link
        color="inherit"
        component={RouterLink}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          fontWeight: 500,
          '&:hover': {
            color: 'primary.main',
          },
        }}
        to={paths.documents.index}
        underline="none"
      >
        <Home sx={{ fontSize: 20, mr: 0.5 }} />
        Home
      </Link>

      {orderedAncestors.map((ancestor) => (
        <Link
          color="inherit"
          component={RouterLink}
          key={ancestor.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            fontWeight: 500,
            '&:hover': {
              color: 'primary.main',
            },
          }}
          to={paths.documents.folder(ancestor.id)}
          underline="none"
        >
          <FolderIcon sx={{ fontSize: 18, mr: 0.5 }} />
          {ancestor.name}
        </Link>
      ))}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.primary',
          fontWeight: 600,
        }}
      >
        <FolderIcon sx={{ fontSize: 18, mr: 0.5 }} />
        <Typography sx={{ fontWeight: 'inherit' }} variant="body1">
          {parent.name}
        </Typography>
      </Box>
    </Breadcrumbs>
  );
}
